<template>
  <div class="dashboard-despatch">
    <div class="row no-gutters" v-if="hasDeliveries">
      <div class="col-3 despatch-tasklist-container">
        <div class="despatch-tasklist">
          <div v-for="task of taskList" :key="task.id" class="despatch-task">
            <div class="badge-area">
              <LabelBadge :labelClass="`label-task-status-${task.status}`">{{task.status | formatLookupName(TaskStatusTypeLookup)}}</LabelBadge>
            </div>
            <div class="title">
              <LinkTask :id="task.id" :name="task.name" />
            </div>
            <div class="item">
              <i class="fas fa-user-circle fa-fw" :style="{ color: getTaskUserColor(task) }"></i> {{task.users.map(u => u.name).join(', ')}}
            </div>
            <div class="item">
              <i class="far fa-truck fa-fw"></i> {{task.resources.map(u => u.name).join(', ')}}
            </div>
            <div class="description">
              {{generateTaskDeliveryDescription(task)}}
            </div>
            <div class="description" v-if="generateTaskDeliveryTimeDetails(task)">
              {{generateTaskDeliveryTimeDetails(task)}}
            </div>
          </div>
        </div>
      </div>
      <div class="col-9">
        <TaskManagerMapView
          ref="mapView"
          :displayRoutes="false"
          :displayUserLocations="true"
          :displayOrders="false"
          mapViewHeight="580px"
          :streetViewControl="false"
          :mapTypeControl="false"
          :adjustBoundsOnLoad="false"
          :mapUserFilter="taskUserMapFilter"
          @mapLoaded="onMapLoaded"
          @mapReady="onMapReady"
        >
          <template slot-scope="{ google, map, mapState }">
            <template v-for="task of taskList">
              <TaskMapRoute
                :key="`task-${task.id}`"
                :routeColour="getTaskUserColor(task)"
                :polylineCode="task.routePolylineCode"
                :itemPoints="getTaskRouteItemPoints(task)"
                :google="google"
                :map="map"
                />

              <template v-for="taskItem of task.taskItems">
                <MapTaskItemMarker
                  :key="`taskItem-${taskItem.id}`"
                  v-if="taskItem.isLocationEnabled && taskItem.locationCoordinate"
                  :position="taskItem.locationCoordinate"
                  :taskItem="taskItem"
                  :google="google"
                  :map="map"
                  :mapState="mapState"
                  :previewMode="true"
                  @click="onMapTaskItemClicked(task, taskItem, $event)"
                />
              </template>
            </template>
          </template>
        </TaskManagerMapView>

        <TaskItemPopover ref="mapTaskItemPopover" :appendToBody="true" />
      </div>
    </div>

    <div v-if="!hasDeliveries" class="text-center">
      <br /><br /><br />
      <h4 class="text-muted">No active tasks available</h4>
    </div>
  </div>
</template>

<style scoped>
.dashboard-despatch {
  position: relative;
  width: 100%;
  height: 100%;
}
.despatch-tasklist-container {
  position: relative;
}
.despatch-tasklist {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-y: auto;
}
.despatch-task {
  padding: 0.75rem;
  border-bottom: 1px solid #E0E0E0;
}
.despatch-task > .badge-area {
  float: right;
}
.despatch-task > .title {
  font-size: 16px;
  font-weight: 600;
}
.despatch-task > .item {
  font-size: 11px;
  padding-left: 8px;
}
.despatch-task > .item > i {
  margin-right: 5px;
}
.despatch-task > .description {
  padding-left: 8px;
  font-size: 12px;
  margin-top: 4px;
  color: #888888;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'

import TimerMixin from '@/mixins/timer'
import GoogleMapMixin from '@/mixins/googleMap'
import DateUtils from '@/utils/dateUtils'

import TaskManagerMixin from '@/modules/tasks/mixins/taskManagerMixin'
import TaskManagerMapView from '@/modules/tasks/components/taskManagerMapView'
import MapTaskItemMarker from '@/modules/tasks/components/mapTaskItemMarker'
import TaskItemPopover from '@/modules/tasks/components/taskItemPopover'
import TaskMapRoute from '@/modules/tasks/components/taskMapRoute'
import MapMarker from '@/components/maps/mapMarker'

import { TaskStatusTypes, TaskStatusTypeLookup, TaskItemStatusTypes } from '@/store/types'

export default {
  name: 'TaskLocationDashboardBox',
  mixins: [TimerMixin, GoogleMapMixin, TaskManagerMixin],
  components: {
    TaskManagerMapView,
    TaskMapRoute,
    MapTaskItemMarker,
    TaskItemPopover,
    MapMarker
  },
  props: {
    taskTypeId: Number
  },
  computed: {
    hasDeliveries() {
      return this.taskList.length > 0
    },
    taskUserMapFilter() {
      let userIdList = []
      this.taskList.forEach(task => {
        if(task.users)
          userIdList = _.concat(userIdList, task.users.map(user => user.id))
      })
      return _.uniq(userIdList)
    },
  },
  created() {
    this.TaskStatusTypeLookup = TaskStatusTypeLookup

    this.debouncedTaskUpdate = _.debounce(() => {
      this.reloadData()
    }, 5000)
  },
  mounted() {
    this.subscribeEndpoint(`/tasks/taskmanager`, () => {
      this.debouncedTaskUpdate()
    })

    // Refresh every minute to update timers
    let deliveryDash = this
    this.createIntervalTimer('deliveryDashboardRefresh', () => {
      deliveryDash.debouncedTaskUpdate()
    }, 60000)

    this.requestData()
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/tasks/taskmanager`)
  },
  data() {
    return {
      taskList: [],
      updateMapZoomToTaskList: true,
      mapLoaded: false
    }
  },
  methods: {
    requestData() {
      this.$apiService.tasksService.getActiveTasksList({ filter: { taskTypeId: this.taskTypeId }})
      .then(response => {
        // If task list size changed (task removed/added), then flag to redo map zooming
        if(this.taskList.length != response.data.length)
          this.updateMapZoomToTaskList = true

        this.taskList = response.data

        if(this.updateMapZoomToTaskList && this.mapLoaded) {
          this.mapZoomToTaskList()
          this.updateMapZoomToTaskList = false
        }
      })
    },
    reloadData() {
      this.requestData()
    },
    onMapLoaded() {
      this.mapLoaded = true

      if(this.updateMapZoomToTaskList && this.mapLoaded) {
        this.mapZoomToTaskList()
        this.updateMapZoomToTaskList = false
      }
    },
    onMapReady() {
      if(this.updateMapZoomToTaskList && this.mapLoaded) {
        this.mapZoomToTaskList()
        this.updateMapZoomToTaskList = false
      }
    },
    mapZoomToTaskList() {
      // Fit to the route
      this.$nextTick(() => {
        let pointList = []

        this.taskList.forEach(task => {
          if(task.routePoints)
            pointList = _.concat(pointList, task.routePoints)
          task.users.forEach(user => {
            if(user.locationCoordinate)
              pointList.push(user.locationCoordinate)
          })
        })

        if(pointList.length >= 2) {
          if(this.$refs.mapView)
            this.$refs.mapView.panToFitPointList(pointList)
        }
      })
    },
    getTaskUserColor(task) {
      if(task.users.length == 0)
        return 'inherit'

      // If it has a primary user assigned, use that colour
      if(task.primaryUser)
        return task.primaryUser.highlightColour

      // Get just first user colour
      return task.users[0].highlightColour
    },
    getTaskRouteItemPoints(task) {
      var itemPoints = []
      if(task.routeStartCoordinate)
        itemPoints.push(task.routeStartCoordinate)

      return itemPoints
    },
    generateTaskDeliveryDescription(task) {
      if(task.status == TaskStatusTypes.completed)
        return `Completed at ${this.formatTime(task.completedDate)}`
      if(task.status == TaskStatusTypes.loading)
        return `Loading`
      if(task.status == TaskStatusTypes.pendingProduction)
        return `Waiting for items to complete production`

      if(task.status == TaskStatusTypes.inProgress) {
        // Get next active task item
        let activeTaskItem = task.taskItems.find(taskItem => taskItem.status == TaskItemStatusTypes.active)
        if(!activeTaskItem) {
          // Get latest completed task
          // Must still be there
          let completedTaskItems = task.taskItems.filter(taskItem => taskItem.status == TaskItemStatusTypes.completed)
          if(completedTaskItems.length > 0) {
            completedTaskItems.sort((ti1, ti2) => {
              return moment(ti2.completedDate).format('X') - moment(ti1.completedDate).format('X')
            })

            return `Finishing up at ${completedTaskItems[0].name}`
          }

          return 'On Route'
        }

        if(activeTaskItem) {
          if(activeTaskItem.isResourceArrived)
            return `At ${activeTaskItem.name}`

          return `On the way to ${activeTaskItem.name}`
        }

        return `On Route`
      }

      return ''
    },
    generateTaskDeliveryTimeDetails(task) {
      if(task.status == TaskStatusTypes.loading) {

        if(DateUtils.areDatesSameDay(moment(), task.scheduleStartDate))
          return `Scheduled departure at ${this.formatTime(task.scheduleStartDate)}`

        return `Scheduled departure on ${this.formatDate(task.scheduleStartDate)}`
      }
      
      if(task.status == TaskStatusTypes.inProgress) {
        let activeTaskItem = task.taskItems.find(taskItem => taskItem.status == TaskItemStatusTypes.active)
        if(activeTaskItem && !activeTaskItem.isResourceArrived) {
          if(activeTaskItem.estimatedArrivalDate && moment(activeTaskItem.estimatedArrivalDate) > moment()) {
            let duration = ''

            let minutesDiff = moment.duration(moment(activeTaskItem.estimatedArrivalDate).diff(moment())).asMinutes()
            // Round up in 5 minute blocks
            minutesDiff = Math.ceil(minutesDiff / 5) * 5

            if(minutesDiff > 60)
              duration = `over an hour`
            else if(minutesDiff == 60)
              duration = `an hour`
            else
              duration = `${minutesDiff} mins`
            
            return `Est. Arrival in ${duration}`
          }
        }
      }

      return ''
    },
    onMapTaskItemClicked(task, taskItem, evt) {
      let mapElement = this.getElementFromClickEvent(evt)

      if(mapElement)
        this.$refs.mapTaskItemPopover.displayFromElement(mapElement, task, taskItem)
    }
  }
}
</script>